import { ConfirmProvider } from 'material-ui-confirm';
import PropTypes from 'prop-types';
import React, { Suspense, lazy, useContext, useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { connect } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { loadLanguages, multilanguage } from 'redux-multilanguage';
import '../src/assets/css/icons.min.css';
import { AuthContext, AuthProvider } from './common/Auth';
import Authentication from './common/Authentication';
import AuthRoute from './common/AuthRoute';
import GlobalLoader from './common/GlobalLoader';
import Loader from './common/Loader';
import NormalizeUrl from './common/NormalizeUrl';
import ChatSupport from './components/chat/Chat';
import LanguageCurrencyChanger from './components/header/sub-components/LanguageCurrencyChanger';
import { GoogleAnalytics } from './components/headerScripts/GoogleAnalitics';
import DynamicBlogComponent from './DynamicBlogComponent';
import DynamicCategoryLayout from './DynamicCategoryLayout';
import DynamicComponent from './DynamicComponent';
import DynamicProductComponent from './DynamicProductComponent';
import EmptyComponent from './EmptyComponent';
import ScrollToTop from './helpers/scroll-top';
import { OrderType } from './models/Order';
import BudgetPage from './pages/budget/BudgetPage';
import NewCheckout from './pages/Checkout/NewCheckout';
import PunchOutForm from './pages/Checkout/PunchOutForm';
import DeactiveStore from './pages/other/deactive';
import AetherInventoryPage from './pages/product-management/AetherInventoryPage';
import InventoryReportPage from './pages/product-management/InventoryReportPage';
import ProductManagementPage from './pages/product-management/ProductManagementPage';
import { receiveAdvanceCurrency } from './redux/actions/currencyActions';
import { useLazyGetStoreDetailsQuery } from './redux/api/advanceApi';
import { useLazyGetPresentationDataQuery } from './redux/api/presentationApi';
import { useLazyGetSettingsDataQuery } from './redux/api/settingsApi';
import TemplateWrapper from './TemplateWrapper';
import ToasterProvider from './ToastProvider';
// product pages
const Product = lazy(() => import('./pages/shop-product/Product'));
const ProductTabLeft = lazy(() => import('./pages/shop-product/ProductTabLeft'));
const ProductTabRight = lazy(() => import('./pages/shop-product/ProductTabRight'));
const ProductSticky = lazy(() => import('./pages/shop-product/ProductSticky'));
const ProductSlider = lazy(() => import('./pages/shop-product/ProductSlider'));
const ProductFixedImage = lazy(() => import('./pages/shop-product/ProductFixedImage'));

// blog pages
const BlogStandard = lazy(() => import('./pages/blog/BlogStandard'));
const BlogNoSidebar = lazy(() => import('./pages/blog/BlogNoSidebar'));
const BlogRightSidebar = lazy(() => import('./pages/blog/BlogRightSidebar'));
const BlogDetailsStandard = lazy(() => import('./pages/blog/BlogDetailsStandard'));
const StaticPage = lazy(() => import('./pages/static-pages/StaticPage'));

// other pages
const About = lazy(() => import('./pages/other/About'));
const Contact = lazy(() => import('./pages/other/Contact'));
const BannerCreateEdit = lazy(() => import('./pages/banner/Banner'));
const BannersList = lazy(() => import('./pages/banner/BannersList'));
const MyAccount = lazy(() => import('./pages/other/MyAccount'));
const Login = lazy(() => import('./pages/other/Login'));
const ForgotPassword = lazy(() => import('./pages/other/ForgotPassword'));
const SetNewPassword = lazy(() => import('./pages/other/SetNewPassword'));
const Register = lazy(() => import('./pages/other/Register'));
const Cart = lazy(() => import('./pages/other/Cart'));
const Wishlist = lazy(() => import('./pages/other/Wishlist'));
const Points = lazy(() => import('./pages/other/Points'));
const Compare = lazy(() => import('./pages/other/Compare'));
const NotFound = lazy(() => import('./pages/other/NotFound'));
const OrdersList = lazy(() => import('./pages/orders/OrdersList'));
const OrdersDetails = lazy(() => import('./pages/orders/OrdersDetails'));
const RewardProgram = lazy(() => import('./pages/rewardprogram/RewardProgram'));
const GiftCardBalance = lazy(() => import('./pages/giftCardBalance/GiftCardBalance'));
const VoucherBalance = lazy(() => import('./pages/voucherBalance/VoucherBalance'));

const App = (props) => {
  const { user } = useContext(AuthContext);

  useEffect(() => {
    props.dispatch(
      loadLanguages({
        languages: {
          en: require('./translations/english.json'),
          fn: require('./translations/french.json'),
          de: require('./translations/germany.json'),
        },
      }),
    );
  }, []);
  const [mainGeneralSetting, setMainGeneralSetting] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mainSetting, setMainSetting] = useState(null);
  const [RegisterLink, setRegisterLink] = useState('null');
  const [IsStoreEnable, setIsStoreEnable] = useState('');
  const [isRedirect, setIsRedirect] = useState(false);
  const [getPresentationData, { data: presentationData }] = useLazyGetPresentationDataQuery();
  const [getStoreDetails] = useLazyGetStoreDetailsQuery();
  const [getSettingsData, { data: settingsData }] = useLazyGetSettingsDataQuery();
  const getThemeId = () => {
    getPresentationData(undefined, true)
      .then((res) => {
        if (res.data.error) {
        } else {
          if (res.data) {
            const customCss = res.data?.customCss;
            for (let file of customCss) {
              if (!file.enabled) continue;
              let link = document.createElement('link');
              link.rel = 'stylesheet';
              link.href = file.url;
              document.head.appendChild(link);
            }
            const themeSettings = res.data?.themeSettings;
            themeSettings.forEach((i) => {
              if (i.settingsName == 'headerMenu') {
                let aboutUs = i?.values[0]?.menuItems.find((menu) => menu.staticName == 'aboutUs');
                let anchorLink =
                  aboutUs?.anchorLink && aboutUs?.anchorLink !== ''
                    ? aboutUs?.anchorLink.charAt(0) == '/'
                      ? aboutUs.anchorLink
                      : `/${aboutUs.anchorLink}`
                    : '/about';
                let contactUs = i?.values[0]?.menuItems.find(
                  (menu) => menu.staticName == 'contactUs',
                );
                let contactAnchorLink =
                  contactUs?.anchorLink && contactUs?.anchorLink !== ''
                    ? contactUs?.anchorLink.charAt(0) == '/'
                      ? contactUs.anchorLink
                      : `/${contactUs.anchorLink}`
                    : '/contact';
                setMainSetting({
                  aboutLink: anchorLink,
                  contactLink: contactAnchorLink,
                });
              }
              if (i.settingsName === 'login') {
                setRegisterLink(i?.values[0]?.isAllowSelfRegistration);
              }
              if (i.settingsName === 'theme' && i.values[0].themeName === 'dark') {
                document.body.classList.add('dark-theme');
              }
            });
          }
        }
      })
      .catch((err) => {
        console.error('err', err);
      });
    localStorage.setItem('googleTranslateLoaded', '0');
  };

  useEffect(() => {
    setLoading(true);
    getSettingsData(undefined, true)
      .then(async (res) => {
        if (res.data && res.data.length > 0) {
          const generalSettings = res.data.find((i) => i.page === 'General')?.values || {};

          const updatedState = {};
          if (generalSettings.isLandingPageEnable !== undefined) {
            updatedState.enableLandingPage = !!generalSettings.isLandingPageEnable;
          }
          if (generalSettings.landingPageRedirect) {
            updatedState.landingPage = generalSettings.landingPageRedirect;
          }
          document.documentElement.style.setProperty(
            '--font-family',
            generalSettings?.defaultFont || '"Poppins", sans-serif',
          );
          document.documentElement.style.setProperty(
            '--themedefaultColor',
            generalSettings?.themedefaultColor || '#FF7F00',
          );
          document.documentElement.style.setProperty(
            '--themedefaulthoverColor',
            generalSettings?.themedefaulthoverColor || '#000',
          );
          document.documentElement.style.setProperty(
            '--themedefaulttextColor',
            generalSettings?.themedefaulttextColor || '#fff',
          );
          document.documentElement.style.setProperty(
            '--product-img-bg',
            generalSettings?.DefaultProductBackgroundColor || '',
          );
          setMainGeneralSetting((prev) => ({ ...prev, ...updatedState }));

          // Currency
          const currencyData = generalSettings.storeDefaultCurrency;
          if (currencyData?.currency) {
            props.dispatch(
              receiveAdvanceCurrency({
                currencytype: 'advanceCurrency',
                symbol: currencyData.symbol,
                currency: currencyData.code,
                currencyRate: 1,
              }),
            );
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log('err', err);
      });
    getThemeId();
    window.addEventListener('beforeunload', () => {
      localStorage.removeItem('redux_localstorage_simple');
    });
  }, []);
  useEffect(() => {
    if (presentationData && settingsData) {
      const loginSettingData = presentationData.themeSettings?.find(
        (i) => i.settingsName === 'login',
      )?.values?.[0];
      const generalSettings = settingsData?.find((i) => i.page === 'General')?.values;
      const isPublic =
        loginSettingData?.isPublic !== undefined
          ? loginSettingData?.isPublic
          : generalSettings?.isPublic ?? true;
      setMainGeneralSetting((prev) => ({ ...prev, isPublic }));
    }
  }, [presentationData, settingsData]);

  useEffect(() => {
    if (IsStoreEnable === '') {
      getStoreDetails(undefined, true).then((res) => {
        if (res.data) {
          setLoading(false);
          let storeData = res.data;
          if (storeData?.enabled === true || storeData?.enabled === false) {
            setIsStoreEnable(storeData.enabled);
          }
        }
      });
    }
  }, [IsStoreEnable]);

  useEffect(() => {
    if (!!mainGeneralSetting) {
      setIsRedirect(!!mainGeneralSetting?.enableLandingPage);
    }
  }, [user, mainGeneralSetting]);

  return (
    <AuthProvider>
      <ConfirmProvider>
        <ToasterProvider />
        <HelmetProvider>
          <BrowserRouter>
            <NormalizeUrl />
            <GlobalLoader>
              <Suspense
                fallback={
                  <div className="flone-preloader-wrapper">
                    <div className="flone-preloader">
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                }
              >
                {loading && (
                  <div className="fixedLoader">
                    <Loader />
                  </div>
                )}

                <ScrollToTop>
                  {IsStoreEnable === false ? (
                    <Routes>
                      <Route path={'/'} element={<DeactiveStore />} />
                    </Routes>
                  ) : (
                    <>
                      <LanguageCurrencyChanger />
                      <Routes>
                        <Route element={<Authentication />}>
                          <Route
                            path="/"
                            element={
                              <TemplateWrapper
                                isRedirect={isRedirect}
                                landingPage={mainGeneralSetting?.landingPage}
                              />
                            }
                          >
                            <Route index element={<DynamicComponent />} />

                            {/* Blog pages */}
                            <Route path={'/blogs'} element={<DynamicBlogComponent />} />
                            <Route path={'/blog-standard'} element={<BlogStandard />} />
                            <Route path={'/blog-no-sidebar'} element={<BlogNoSidebar />} />
                            <Route path={'/blog-right-sidebar'} element={<BlogRightSidebar />} />
                            <Route path={'/blogs/:url'} element={<BlogDetailsStandard />} />
                            <Route path={'/page/:pageName'} element={<StaticPage />} />

                            <Route path={'/cb'} element={<EmptyComponent />} />
                            <Route path={'/activate'} element={<EmptyComponent />} />
                            <Route path={'/products'} element={<DynamicCategoryLayout />} />

                            {/* Shop product pages */}
                            <Route
                              path={'/products/:category'}
                              element={<DynamicCategoryLayout />}
                            />
                            <Route
                              path={'/products/:category/:childCategory'}
                              element={<DynamicCategoryLayout />}
                            />
                            <Route
                              path={'/products/:category/:childCategory/:subChildCategory'}
                              element={<DynamicCategoryLayout />}
                            />
                            <Route
                              path={
                                '/products/:category/:childCategory/:subChildCategory/:subChildCategoryLevel4'
                              }
                              element={<DynamicCategoryLayout />}
                            />
                            <Route
                              path={
                                '/products/:category/:childCategory/:subChildCategory/:subChildCategoryLevel4/:subChildCategoryLevel5'
                              }
                              element={<DynamicCategoryLayout />}
                            />
                            <Route
                              path={
                                '/products/:category/:childCategory/:subChildCategory/:subChildCategoryLevel4/:subChildCategoryLevel5/:subChildCategoryLevel6'
                              }
                              element={<DynamicCategoryLayout />}
                            />
                            <Route
                              path={
                                '/products/:category/:childCategory/:subChildCategory/:subChildCategoryLevel4/:subChildCategoryLevel5/:subChildCategoryLevel6/:subChildCategoryLevel7'
                              }
                              element={<DynamicCategoryLayout />}
                            />

                            <Route path={'/product/:id'} element={<DynamicProductComponent />} />
                            <Route path={'/product-tab-left/:id'} element={<ProductTabLeft />} />
                            <Route path={'/product-tab-right/:id'} element={<ProductTabRight />} />
                            <Route path={'/product-sticky/:id'} element={<ProductSticky />} />
                            <Route path={'/product-slider/:id'} element={<ProductSlider />} />
                            <Route
                              path={'/product-fixed-image/:id'}
                              element={<ProductFixedImage />}
                            />

                            {/* Banner add/edit pages */}
                            <Route path={'/banner'} element={<BannersList />} />
                            <Route path={'/banner/create'} element={<BannerCreateEdit />} />
                            <Route path={'/banner/edit/:id'} element={<BannerCreateEdit />} />

                            {/* Other pages */}
                            <Route
                              path={`${mainSetting?.aboutLink ? mainSetting?.aboutLink : `/about`}`}
                              element={<About />}
                            />
                            <Route
                              path={`${
                                mainSetting?.contactLink ? mainSetting?.contactLink : `/contact`
                              }`}
                              element={<Contact />}
                            />
                            <Route path={'/my-account'} element={<MyAccount />} />
                            <Route path={'/cart'} element={<Cart />} />
                            <Route path={'/orders'} element={<OrdersList />} />
                            <Route path={'/orders/:id'} element={<OrdersDetails />} />
                            <Route path={'/wishlist'} element={<Wishlist />} />
                            <Route path={'/wallet'} element={<Points />} />
                            <Route path={'/compare'} element={<Compare />} />
                            <Route
                              path={'/checkout'}
                              element={<NewCheckout orderType={OrderType.STORE_ORDER} />}
                            />
                            <Route
                              path={'/newcheckout'}
                              element={<Navigate to="/checkout" replace />}
                            />
                            <Route
                              path={'/quote'}
                              element={<NewCheckout orderType={OrderType.QUOTE} />}
                            />
                            <Route path={'/punchout'} element={<PunchOutForm />} />
                            <Route path={'/rewardprogram'} element={<RewardProgram />} />
                            <Route path={'/giftcardbalance'} element={<GiftCardBalance />} />
                            <Route path={'/voucherbalance'} element={<VoucherBalance />} />
                            <Route path={'/budgets'} element={<BudgetPage />} />
                            <Route
                              path={'/product-management'}
                              element={<ProductManagementPage />}
                            />
                            <Route
                              path={'/product-management/:id/aether-inventory'}
                              element={<AetherInventoryPage />}
                            />
                            <Route
                              path={'/product-management/inventory-report'}
                              element={<InventoryReportPage />}
                            />
                          </Route>
                        </Route>

                        <Route path="/" element={<TemplateWrapper />}>
                          {/*Non authenticated routes*/}
                          <Route
                            path={'/login'}
                            element={
                              <AuthRoute>
                                {' '}
                                <Login />{' '}
                              </AuthRoute>
                            }
                          />
                          {(RegisterLink === true || RegisterLink === false) && (
                            <Route path={'/register'} element={<Register />} />
                          )}
                          <Route
                            path={'/forgotpassword'}
                            element={
                              <AuthRoute>
                                {' '}
                                <ForgotPassword />{' '}
                              </AuthRoute>
                            }
                          />
                          <Route
                            path={'/setNewPassword/:id'}
                            element={
                              <AuthRoute>
                                {' '}
                                <SetNewPassword />{' '}
                              </AuthRoute>
                            }
                          />
                          <Route path={'/notFound'} element={<NotFound />} />
                        </Route>
                      </Routes>
                    </>
                  )}
                  <ChatSupport />
                  <GoogleAnalytics />
                </ScrollToTop>
              </Suspense>
            </GlobalLoader>
          </BrowserRouter>
        </HelmetProvider>
      </ConfirmProvider>
    </AuthProvider>
  );
};

App.propTypes = {
  dispatch: PropTypes.func,
};

export default React.memo(connect()(multilanguage(App)));
